import { FC, useMemo, useRef, useState } from 'react'
import type { Analytic } from '@app/types'
import {
  dataListStyle,
  pagingListStyle,
} from '@app/components/general/cells/card/head-style'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { PagingSearch } from '../../lists/render/issues/paging-search'
import { AnalyticsList } from '../../lists/render/issues/analytics-list'
import { InnerWrapper } from '../../lists/list-wrapper'
import { useSearchQuery } from '@app/data/local/useSearchQuery'
import { useSortDirection } from '../../lists/render/issues/use-sort-direction'

type AnalyticsPagingProps = {
  pageUrl?: string
  liveData?: Analytic[]
  open?: boolean
  extraActive?: boolean | string
  domain?: string
  filterActive?: 'errors' | 'warnings'
}

// paging issues for website dashboard cell
export const RenderInnerAnalyticsPagingMock: FC<AnalyticsPagingProps> = ({
  liveData,
  pageUrl,
  open: defaultOpen,
  extraActive,
  domain: _domain,
  filterActive: _filterActive,
}) => {
  const { t } = useTranslation('dashboard')
  const { search, visible, onSearchEvent } = useSearchQuery()
  const [issueVisible, setIssueVisible] = useState<boolean>(!!defaultOpen)

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [issueIndex, setIndex] = useState<number>(0)
  const offsetTracking = useRef<{ last: number; next: number }>({
    last: 0,
    next: 0,
  })

  const searchRef = useRef<HTMLInputElement | null>(null)

  const variables = useSortDirection(pageUrl)

  const { issueSource, liveDataExist } = useMemo(() => {
    const liveDataExist = liveData?.length
    const dataSource = search
      ? liveData?.filter((data) => data.pageUrl.includes(search))
      : liveData

    // Apply sorting rules here from live data.
    if (dataSource && variables.sort) {
      if (variables.sort === 'asc') {
        dataSource.sort((a: any, b: any) => {
          const first = a[variables.sortType || 'errorCount']
          const next = b[variables.sortType || 'errorCount']

          return first - next
        })
      } else {
        dataSource.sort((a: any, b: any) => {
          const first = a[variables.sortType || 'errorCount']
          const next = b[variables.sortType || 'errorCount']

          return next - first
        })
      }
    }

    return {
      issueSource: dataSource || [],
      liveDataExist,
    }
  }, [liveData, search, variables])

  const [issueList, stats] = useMemo(() => {
    let base = (issueIndex + 1) * 10

    if (liveDataExist && issueIndex) {
      // set the last offset value
      offsetTracking.current.last = offsetTracking.current.next
      base += offsetTracking.current.next
    }

    const items: Analytic[] = new Array(Math.max(issueSource.length, 10))

    let errorCount = 0
    let warningCount = 0
    // iterator
    let j = 0
    let i = base - 10
    // offset position
    let offsetTracker = 0

    // loop until ten items filled
    for (; j < 10; i++) {
      const item = issueSource[i]

      if (!item || j === 10) {
        break
      }

      if (item && typeof item.warningCount === 'number') {
        items[j] = item
        errorCount += item.errorCount
        warningCount += item.warningCount
        j++
      } else {
        offsetTracker += 1
      }
    }

    offsetTracking.current.next = offsetTracker
    items.length = j
    // PASS IN variables TO RE-UPDATE - IGNORE UNUSED HOOK WARNING
    return [items, { errorCount, warningCount }]
  }, [issueIndex, issueSource, liveDataExist, offsetTracking, variables])

  const onPrevSelect = () => {
    if (issueIndex) {
      if (liveDataExist) {
        offsetTracking.current.next = offsetTracking.current.last
      }
      setIndex((x: number) => x - 1)
      setSelectedIndex(undefined)
    }
  }

  const idx = (issueIndex + 1) * 10
  const blocked = issueSource.length < idx || issueList.length < 10

  const onLoadEvent = async () => {
    // get the next set of data
    if (idx === issueSource.length) {
      //   onLoadMore && (await onLoadMore())
    }
    setIndex((x: number) => x + 1)
    setSelectedIndex(undefined)
  }

  const onSearchSubmit = async (e: React.FormEvent) => {
    e?.preventDefault()
    if (!search) {
      setIndex(0)
      //   await onLoadMore()
    } else {
      //   await onLoadMore()
    }
  }

  return (
    <>
      <div className='flex flex-col place-content-around'>
        <div className={dataListStyle}>
          <InnerWrapper data={issueSource.length} loading={false}>
            <ul className={issueVisible ? 'py-0.5' : pagingListStyle}>
              {issueList.map((page, index) => (
                <AnalyticsList
                  key={page?._id || page.pageUrl}
                  totalErrors={stats.errorCount}
                  open={defaultOpen}
                  extraActive={extraActive}
                  setSelectedIndex={setSelectedIndex}
                  selectedIndex={selectedIndex}
                  index={index}
                  t={t}
                  setIssueVisible={setIssueVisible}
                  demo
                  {...page}
                />
              ))}
            </ul>
          </InnerWrapper>
        </div>
        <PagingSearch
          searchVisible={visible}
          onLoadEvent={onLoadEvent}
          onPrevSelect={onPrevSelect}
          onSearchEvent={onSearchEvent}
          onSearchSubmit={onSearchSubmit}
          loading={false}
          blocked={blocked}
          ref={searchRef}
          issueIndex={issueIndex}
          source={issueSource}
          visiblity={!issueVisible}
          t={t}
        />
      </div>
    </>
  )
}
